import { spawn, type ChildProcessWithoutNullStreams } from "node:child_process";
import path from "node:path";
import type { ToolDefinition } from "@mewbleh/nori-plugin-sdk";
import { resolveSecret, type McpServerConfig } from "./config.js";
import { readResponseText } from "./http.js";

type JsonRpcResponse = { jsonrpc: "2.0"; id: number | string; result?: unknown; error?: { code: number; message: string; data?: unknown } };
type Pending = { resolve(value: unknown): void; reject(error: Error): void; timer: NodeJS.Timeout };
export type McpTool = { name: string; description?: string; inputSchema?: Record<string, unknown> };

function loopback(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1" || hostname.endsWith(".localhost");
}

function validateRemoteUrl(raw: string): URL {
  const url = new URL(raw);
  if (url.protocol !== "https:" && !(url.protocol === "http:" && loopback(url.hostname))) throw new Error("Remote MCP URLs must use HTTPS (HTTP is allowed only on loopback)");
  return url;
}

function parseSse(text: string): unknown {
  for (const block of text.split(/\r?\n\r?\n/u)) {
    const data = block.split(/\r?\n/u).filter((line) => line.startsWith("data:")).map((line) => line.slice(5).trim()).join("\n");
    if (data && data !== "[DONE]") return JSON.parse(data) as unknown;
  }
  throw new Error("MCP server returned an empty event stream");
}

export class McpClient {
  readonly #pending = new Map<number, Pending>();
  #nextId = 1;
  #child?: ChildProcessWithoutNullStreams;
  #stdoutBuffer = "";
  #stderr = "";
  #sessionId?: string;
  #headers: Record<string, string> = {};
  #closed = false;

  private constructor(
    readonly name: string,
    readonly config: McpServerConfig,
    readonly env: NodeJS.ProcessEnv,
    readonly fetch: typeof globalThis.fetch,
  ) {}

  static async connect(name: string, config: McpServerConfig, options: { env?: NodeJS.ProcessEnv; fetch?: typeof globalThis.fetch } = {}): Promise<McpClient> {
    const client = new McpClient(name, config, options.env ?? process.env, options.fetch ?? globalThis.fetch);
    await client.#start();
    return client;
  }

  async #start(): Promise<void> {
    const transport = this.config.transport ?? (this.config.url ? "streamable-http" : "stdio");
    if (transport === "stdio") {
      if (!this.config.command) throw new Error(`MCP server ${this.name} has no command`);
      const child = spawn(this.config.command, this.config.args ?? [], {
        cwd: this.config.cwd ? path.resolve(this.config.cwd) : undefined,
        env: { ...this.env, ...this.config.env },
        detached: process.platform !== "win32",
        stdio: ["pipe", "pipe", "pipe"],
        windowsHide: true,
      });
      this.#child = child;
      child.stdout.setEncoding("utf8");
      child.stderr.setEncoding("utf8");
      child.stdout.on("data", (chunk: string) => this.#onStdout(chunk));
      child.stderr.on("data", (chunk: string) => { this.#stderr = `${this.#stderr}${chunk}`.slice(-16_384); });
      child.on("error", (error) => this.#rejectAll(error));
      child.on("exit", (code, signal) => this.#rejectAll(new Error(`MCP server ${this.name} exited (${signal ?? code ?? "unknown"})${this.#stderr ? `: ${this.#stderr.trim()}` : ""}`)));
    } else {
      if (!this.config.url) throw new Error(`MCP server ${this.name} has no URL`);
      validateRemoteUrl(this.config.url);
      for (const [header, value] of Object.entries(this.config.headers ?? {})) {
        const resolved = typeof value === "string" ? value : await resolveSecret(value, this.env);
        if (resolved) this.#headers[header] = resolved;
      }
    }
    await this.request("initialize", {
      protocolVersion: "2025-06-18",
      capabilities: {},
      clientInfo: { name: "nori", version: "0.3.0" },
    });
    await this.notify("notifications/initialized", {});
  }

  #onStdout(chunk: string): void {
    this.#stdoutBuffer += chunk;
    if (this.#stdoutBuffer.length > 16 * 1024 * 1024) {
      this.#rejectAll(new Error(`MCP server ${this.name} emitted an oversized frame`));
      this.close();
      return;
    }
    while (true) {
      const newline = this.#stdoutBuffer.indexOf("\n");
      if (newline < 0) break;
      const line = this.#stdoutBuffer.slice(0, newline).trim();
      this.#stdoutBuffer = this.#stdoutBuffer.slice(newline + 1);
      if (!line) continue;
      try {
        const message = JSON.parse(line) as JsonRpcResponse & { method?: string };
        if (message.method && message.id !== undefined) {
          this.#write({ jsonrpc: "2.0", id: message.id, error: { code: -32601, message: "Nori MCP client does not implement server-initiated requests" } });
          continue;
        }
        if (message.id === undefined) continue;
        const pending = this.#pending.get(Number(message.id));
        if (!pending) continue;
        this.#pending.delete(Number(message.id));
        clearTimeout(pending.timer);
        if (message.error) pending.reject(Object.assign(new Error(message.error.message), { code: message.error.code, data: message.error.data }));
        else pending.resolve(message.result);
      } catch (error) {
        this.#rejectAll(new Error(`Invalid JSON-RPC from MCP server ${this.name}: ${(error as Error).message}`));
      }
    }
  }

  #write(message: unknown): void {
    const child = this.#child;
    if (!child || child.stdin.destroyed) throw new Error(`MCP server ${this.name} is not connected`);
    child.stdin.write(`${JSON.stringify(message)}\n`);
  }

  async #remote(message: unknown, expectResponse: boolean): Promise<unknown> {
    const url = validateRemoteUrl(this.config.url!);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.config.requestTimeoutMs ?? 30_000);
    try {
      const response = await this.fetch(url, {
        method: "POST",
        redirect: "error",
        headers: {
          accept: "application/json, text/event-stream",
          "content-type": "application/json",
          ...this.#headers,
          ...(this.#sessionId ? { "mcp-session-id": this.#sessionId } : {}),
        },
        body: JSON.stringify(message),
        signal: controller.signal,
      });
      if (!response.ok && !(response.status === 202 && !expectResponse)) throw new Error(`MCP HTTP ${response.status}: ${(await readResponseText(response, 64 * 1024, "MCP error response")).slice(0, 2_048)}`);
      const sessionId = response.headers.get("mcp-session-id");
      if (sessionId) this.#sessionId = sessionId;
      if (!expectResponse || response.status === 202) return undefined;
      const text = await readResponseText(response, 16 * 1024 * 1024, "MCP response");
      const parsed = response.headers.get("content-type")?.includes("text/event-stream") ? parseSse(text) : JSON.parse(text) as unknown;
      const result = parsed as JsonRpcResponse;
      if (result.error) throw Object.assign(new Error(result.error.message), { code: result.error.code, data: result.error.data });
      return result.result;
    } finally {
      clearTimeout(timer);
    }
  }

  request(method: string, params: unknown = {}): Promise<unknown> {
    if (this.#closed) return Promise.reject(new Error(`MCP client ${this.name} is closed`));
    const id = this.#nextId++;
    const message = { jsonrpc: "2.0" as const, id, method, params };
    if (!this.#child) return this.#remote(message, true);
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.#pending.delete(id);
        reject(new Error(`MCP request timed out: ${this.name}/${method}`));
      }, this.config.requestTimeoutMs ?? 30_000);
      this.#pending.set(id, { resolve, reject, timer });
      try { this.#write(message); }
      catch (error) { clearTimeout(timer); this.#pending.delete(id); reject(error); }
    });
  }

  async notify(method: string, params: unknown = {}): Promise<void> {
    const message = { jsonrpc: "2.0" as const, method, params };
    if (this.#child) this.#write(message);
    else await this.#remote(message, false);
  }

  async listTools(): Promise<McpTool[]> {
    const result = await this.request("tools/list", {}) as { tools?: McpTool[] };
    return Array.isArray(result?.tools) ? result.tools : [];
  }

  callTool(name: string, args: unknown): Promise<unknown> {
    return this.request("tools/call", { name, arguments: args });
  }

  #rejectAll(error: Error): void {
    for (const pending of this.#pending.values()) {
      clearTimeout(pending.timer);
      pending.reject(error);
    }
    this.#pending.clear();
  }

  close(): void {
    if (this.#closed) return;
    this.#closed = true;
    this.#rejectAll(new Error(`MCP client ${this.name} closed`));
    const child = this.#child;
    if (child && child.exitCode == null) {
      if (process.platform === "win32") child.kill("SIGTERM");
      else if (child.pid) {
        try { process.kill(-child.pid, "SIGTERM"); }
        catch { child.kill("SIGTERM"); }
      }
    }
  }
}

export function projectMcpToolName(server: string, tool: string): string {
  return `${server}__${tool}`.replace(/[^a-zA-Z0-9_-]/gu, "_").slice(0, 128);
}

export function mcpToolDefinition(server: string, tool: McpTool): ToolDefinition {
  return {
    name: projectMcpToolName(server, tool.name),
    description: `[MCP:${server}] ${tool.description ?? tool.name}`,
    inputSchema: tool.inputSchema ?? { type: "object", additionalProperties: true },
  };
}

export function toolAllowed(config: McpServerConfig, name: string): boolean {
  const include = config.toolFilter?.include;
  const exclude = config.toolFilter?.exclude;
  if (include?.length && !include.includes(name)) return false;
  return !exclude?.includes(name);
}
